import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Box,
  Stack,
} from "@mui/material";

const GeneralInfo = ({ profileData }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [role, setRole] = useState("");
  const [createdAt, setCreatedAt] = useState(null);
  const [profileUpdatedAt, setProfileUpdatedAt] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const token = localStorage.getItem("access_token");

  // Initialize state directly from profileData
  useEffect(() => {
    if (profileData?.user) {
      const userData = {
        firstName: profileData.user.fname || "",
        lastName: profileData.user.lname || "",
        userPhone: profileData.user.user_phone || "Not Added",
      };
      setOriginalData(userData);
      setFirstName(userData.firstName);
      setLastName(userData.lastName);
      setUserPhone(userData.userPhone);
      setEmail(profileData.user.email || "");
      setRole(profileData.user.user_role || "");
      setCreatedAt(
        profileData.user.created_at
          ? new Date(profileData.user.created_at).toLocaleDateString()
          : null
      );
      setProfileUpdatedAt(
        profileData.user.profile_updated_at
          ? new Date(profileData.user.profile_updated_at).toLocaleDateString()
          : null
      );
    }
  }, [profileData]);

  const handleEditClick = () => {
    setOriginalData({ firstName, lastName, userPhone });
    setIsEditable(true);
  };

  const handleCancel = () => {
    setFirstName(originalData.firstName);
    setLastName(originalData.lastName);
    setUserPhone(originalData.userPhone);
    setIsEditable(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    const apiUrl = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/profile`;
  
    try {
      const res = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          fname: firstName,
          lname: lastName,
          user_phone: userPhone,
        }),
      });
  
      const data = await res.json();
  
      if (!res.ok) {
        throw new Error(data.message || "Failed to update profile");
      }

      // Update the original data to reflect saved changes
      setOriginalData({ firstName, lastName, userPhone });
      setIsEditable(false);
  
      setSnackbar({
        open: true,
        message: "Profile updated successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const renderRow = (label, value, isEditableField, setter) => (
    <Box display="flex" alignItems="center" mb={2}>
      <Typography
        variant="subtitle1"
        sx={{ width: "150px", fontWeight: "bold" }}
      >
        {label}
      </Typography>
      {isEditable && isEditableField ? (
        <TextField
          value={value.startsWith("Not ") ? "" : value}
          onChange={(e) => setter(e.target.value)}
          variant="outlined"
          size="small"
        />
      ) : (
        <Typography variant="body1">{value || "N/A"}</Typography>
      )}
    </Box>
  );

  return (
    <Container maxWidth="md">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Typography
        variant="h5"
        sx={{ marginTop: "10px", marginLeft: "1px" }}
        gutterBottom
      >
        General Information
      </Typography>

      <Grid container spacing={2} sx={{ marginTop: "10px" }}>
        <Grid item xs={12} sm={6}>
          {renderRow("First Name:", firstName, true, setFirstName)}
          {renderRow("Email:", email, false)}
          {renderRow("Role:", role, false)}
          {createdAt != null ? renderRow("Created At:", createdAt, false) : ""}
        </Grid>
        <Grid item xs={12} sm={6}>
          {renderRow("Last Name:", lastName, true, setLastName)}
          {renderRow("User Phone:", userPhone, true, setUserPhone)}
          {profileUpdatedAt != null
            ? renderRow("Updated At:", profileUpdatedAt, false)
            : ""}
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
        {!isEditable ? (
          <Button
            variant="contained"
            className="athrv-dash-primary-button"
            onClick={handleEditClick}
          >
            Edit
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="success"
              className="athrv-dash-primary-button"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
          </>
        )}
      </Stack>
    </Container>
  );
};

export default GeneralInfo;